import { createContext, useContext, useEffect, useState } from "react"
import type { ReactNode } from "react"
import { apiClient } from "./api_client"

export interface Feature {
  id: number
  key: string
  name?: string
  is_enabled: number | string | boolean
  created_at?: string
  updated_at?: string
}

export type FeatureMap = Record<string, boolean>

// `is_enabled` arrives as a number (1/0) from the API, but tolerate "1"/true too.
function isEnabled(value: Feature["is_enabled"]): boolean {
  return value === 1 || value === "1" || value === true
}

const FeaturesContext = createContext<FeatureMap>({})

export const FeaturesProvider = ({ children }: { children: ReactNode }) => {
  const [features, setFeatures] = useState<FeatureMap>({})

  useEffect(() => {
    let alive = true
    apiClient.get<Feature[]>('/features').then(res => {
      if (!alive) return
      const list = Array.isArray(res.data) ? res.data : []
      const map: FeatureMap = {}
      list.forEach(feature => {
        if (feature && feature.key) {
          map[feature.key] = isEnabled(feature.is_enabled)
        }
      })
      setFeatures(map)
    }).catch(() => { })
    return () => { alive = false }
  }, [])

  return <FeaturesContext.Provider value={features}>{children}</FeaturesContext.Provider>
}

// Unknown flags stay disabled, so gated UI never flashes before /features resolves.
export function useFeature(key: string): boolean {
  return useContext(FeaturesContext)[key] === true
}
